import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { asArray } from "@/lib/api";
import { useFetch } from "@/lib/hooks";

const PERIODS = [{ v: 7, l: "7 days" }, { v: 30, l: "30 days" }, { v: 0, l: "All time" }];

const Bars = ({ rows, testId, link }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ol className="mt-3 space-y-2.5" data-testid={testId}>
      {rows.map((r, i) => (
        <li key={r.name} className="text-sm" data-testid={`${testId}-row-${i}`}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate font-medium text-ph-ink">{link ? <Link to={`/brands/${r.slug}`} className="hover:underline">{r.name}</Link> : r.name}</span>
            <span className="shrink-0 font-mono-ph text-xs text-ph-muted">{r.count}{r.whatsapp ? ` · ${r.whatsapp} on WhatsApp` : ""}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-ph-raised">
            <div className="h-full rounded-full bg-ph-accent transition-[width] duration-700" style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ol>
  );
};

export function LeadInsights() {
  const [days, setDays] = useState(30);
  const { data, loading } = useFetch(`/admin/leads/stats?days=${days}`, null);
  const brands = asArray(data?.brands);
  const cities = asArray(data?.cities);
  return (
    <div className="rounded-3xl border border-ph-line bg-ph-surface p-6" data-testid="admin-lead-insights">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-display text-lg font-bold text-ph-ink"><Phone className="h-4 w-4 text-ph-accent" /> Price enquiries by brand and city
            <span className="font-mono-ph text-sm font-normal text-ph-muted" data-testid="lead-insights-total">{loading ? "" : data?.total ?? 0}</span></p>
          <p className="text-sm text-ph-muted">Where the numbers left in the download pop-up come from.</p>
        </div>
        <div className="flex rounded-full border border-ph-line p-1" role="group" aria-label="Period">
          {PERIODS.map((p) => (
            <button key={p.v} type="button" onClick={() => setDays(p.v)} aria-pressed={days === p.v} data-testid={`lead-insights-period-${p.v}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${days === p.v ? "bg-ph-lime text-ph-navy" : "text-ph-muted hover:text-ph-ink"}`}>{p.l}</button>
          ))}
        </div>
      </div>
      {loading ? <div className="ph2-shimmer mt-6 h-40 rounded-2xl" /> : !data?.total ? (
        <p className="mt-6 rounded-2xl border border-dashed border-ph-line py-12 text-center text-sm text-ph-muted" data-testid="lead-insights-empty">
          No enquiries in this period yet. This fills in as visitors leave their number before downloading.
        </p>
      ) : (
        <div className="mt-6 grid gap-8 md:grid-cols-2">
          <div><p className="font-mono-ph text-[11px] uppercase tracking-wider text-ph-muted">Top brands</p><Bars rows={brands} testId="lead-insights-brands" link /></div>
          <div><p className="flex items-center gap-1 font-mono-ph text-[11px] uppercase tracking-wider text-ph-muted"><MapPin className="h-3 w-3" /> Top cities</p><Bars rows={cities} testId="lead-insights-cities" /></div>
        </div>
      )}
    </div>
  );
}
